import { isApiError } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Dialog, DialogContent, Icon, Skeleton } from "@sagot/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { LeadDetail } from "../../engage/leads/lead-types";
import { LEAD_CONVERT_REASON } from "./lead-convert-constants";
import { LeadConvertFields } from "./lead-convert-fields";
import { convertBody, convertProblems, hasDuplicates, initialConvertForm, type ConvertForm, type ConvertProblem } from "./lead-convert-model";
import { LeadConvertPreview } from "./lead-convert-preview";
import type { LeadConversion, LeadProposal } from "./lead-convert-types";
import { useConvertLead, useLeadConvertPreview } from "./use-lead-conversion";

type Step = "form" | "preview" | "done";

function useConvertErrors(): (error: unknown) => string {
  const { t } = useTranslation();
  const fallback = useErrorMessage();
  return useCallback(
    (error) => {
      const reason = isApiError(error) ? error.details.reason : undefined;
      if (reason === LEAD_CONVERT_REASON.alreadyConverted) return t("service.leadConvert.errors.alreadyConverted");
      if (reason === LEAD_CONVERT_REASON.duplicatesFound) return t("service.leadConvert.errors.duplicates");
      if (isApiError(error) && error.status === 403) return t("service.leadConvert.errors.rights");
      if (isApiError(error) && error.status === 404) return t("service.leadConvert.errors.notFound");
      return fallback(error);
    },
    [fallback, t],
  );
}

function Done({ result }: { result: LeadConversion }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="space-y-4" data-testid="lead-convert-done">
      <p role="status" className="flex items-start gap-2 rounded-md bg-success-soft px-3 py-2.5 text-sm font-semibold text-success">
        <Icon name="check-circle" className="mt-0.5 size-4 shrink-0" />
        <span>{result.organisation_created ? t("service.leadConvert.done.created", { name: result.organisation_name }) : t("service.leadConvert.done.linked", { name: result.organisation_name })}</span>
      </p>
      <ul className="space-y-2 text-sm">
        <li className="flex flex-wrap items-center gap-2">
          <Icon name="building-2" className="size-4 text-fg-subtle" />
          <AppLink href={consoleRoutes.organisations(summary.slug, result.organisation_id)} className="font-semibold text-primary-ink underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-primary">
            {t("service.leadConvert.done.openOrganisation", { name: result.organisation_name })}
          </AppLink>
        </li>
        {result.contact_id ? (
          <li className="flex flex-wrap items-center gap-2">
            <Icon name="contact" className="size-4 text-fg-subtle" />
            <AppLink href={consoleRoutes.contacts(summary.slug, result.contact_id)} className="font-semibold text-primary-ink underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-primary">
              {t("service.leadConvert.done.openContact", { name: result.contact_name ?? "" })}
            </AppLink>
          </li>
        ) : null}
      </ul>
      <p className="text-xs text-fg-muted">{t("service.leadConvert.done.recorded")}</p>
    </div>
  );
}

/**
 * "Convert to organisation" (ORG-019): fill in the organisation (the lead has no company) and check the contact, preview what would
 * be made and which duplicates were found, then convert. A duplicate stops the conversion until the person links the lead to what exists.
 */
export function LeadConvertDialog({ lead, onClose }: { lead: LeadDetail; onClose: () => void }) {
  const { t } = useTranslation();
  const describe = useConvertErrors();
  const preview = useLeadConvertPreview(lead.id);
  const convert = useConvertLead(lead.id);
  const [step, setStep] = useState<Step>("form");
  const region = useRef<HTMLDivElement>(null);
  const moved = useRef(false);
  const [form, setForm] = useState<ConvertForm>(() => initialConvertForm(lead));
  const [attempted, setAttempted] = useState(false);
  const [proposal, setProposal] = useState<LeadProposal | null>(null);
  const [link, setLink] = useState(false);
  const [result, setResult] = useState<LeadConversion | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const problems: readonly ConvertProblem[] = attempted ? convertProblems(form) : [];
  // The button that was pressed is replaced by the next step's: focus goes to the new step, not to the page behind the dialog.
  useEffect(() => {
    if (moved.current) region.current?.focus({ preventScroll: true });
    moved.current = true;
  }, [step]);

  const runPreview = () => {
    setAttempted(true);
    if (convertProblems(form).length > 0) return;
    setFailure(null);
    preview.mutate(convertBody(form, false), {
      onSuccess: (read) => { setProposal(read); setLink(false); setStep("preview"); },
      onError: (error) => { setFailure(describe(error)); },
    });
  };
  const apply = () => {
    setFailure(null);
    convert.mutate(convertBody(form, link), {
      onSuccess: (done) => { setResult(done); setStep("done"); },
      onError: (error) => { setFailure(describe(error)); },
    });
  };
  const blocked = proposal?.converted_to !== null || (hasDuplicates(proposal) && !link);

  const footer =
    step === "form" ? (
      <>
        <Button type="button" variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
        <Button type="button" loading={preview.isPending} onClick={runPreview} data-testid="lead-preview">
          {t("service.leadConvert.actions.preview")}
          <Icon name="arrow-right" />
        </Button>
      </>
    ) : step === "preview" ? (
      <>
        <Button type="button" variant="secondary" disabled={convert.isPending} onClick={() => { setStep("form"); setFailure(null); }}>
          <Icon name="arrow-left" />
          {t("service.leadConvert.actions.back")}
        </Button>
        <Button type="button" loading={convert.isPending} disabled={blocked} onClick={apply} data-testid="lead-convert">
          <Icon name="building-2" />
          {t("service.leadConvert.actions.convert")}
        </Button>
      </>
    ) : (
      <Button type="button" onClick={onClose} data-testid="lead-convert-close">{t("common.close")}</Button>
    );

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !convert.isPending) onClose(); }}>
      <DialogContent className="max-w-2xl" title={t("service.leadConvert.title", { name: lead.name })} description={step === "form" ? t("service.leadConvert.hint") : step === "preview" ? t("service.leadConvert.previewHint") : undefined} closeLabel={t("common.close")} footer={footer}>
        <div ref={region} tabIndex={-1} role="group" aria-label={t(`service.leadConvert.step.${step}`)} className="space-y-4 outline-none">
          {failure ? <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm font-semibold text-danger" data-testid="lead-convert-problem">{failure}</p> : null}
          {step === "form" ? <LeadConvertFields form={form} problems={problems} onChange={(changes) => { setForm((current) => ({ ...current, ...changes })); }} /> : null}
          {step === "preview" && proposal && convert.isPending ? (
            <div role="status" aria-busy="true" className="space-y-2">
              <p className="text-sm font-semibold">{t("service.leadConvert.working")}</p>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : null}
          {step === "preview" && proposal && !convert.isPending ? <LeadConvertPreview proposal={proposal} organisationName={form.organisationName.trim()} link={link} onLink={setLink} /> : null}
          {step === "done" && result ? <Done result={result} /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
